import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';
import type {
  MasterListParams,
  MasterListResponse,
  MasterItemResponse,
} from '@/features/master/types';

/**
 * 求人のステータス。
 */
export type JobStatus = 'DRAFT' | 'PUBLISHED' | 'CLOSED';

/**
 * 求人の勤務地。
 */
export interface JobLocation {
  prefecture: string;
  city: string | null;
}

/**
 * 求人のドメイン型。admin-console-api の jobs テーブル準拠。
 * jobImageUrl は廃止済みのため含めない。scrapedJobId・normalized 系は読み取り専用フィールド。
 */
export interface Job {
  id: string;
  companyId: string;
  companyName?: string;
  scrapedJobId: string | null;
  title: string;
  description: string | null;
  summary: string | null;
  employmentType: 'FULL_TIME' | 'PART_TIME' | 'TEMPORARY' | 'CONTRACTOR';
  salaryType: 'HOURLY' | 'DAILY' | 'MONTHLY' | 'YEARLY';
  minSalary: number | null;
  maxSalary: number | null;
  normalizedMinMonthlySalary: number | null;
  normalizedMaxMonthlySalary: number | null;
  locations: JobLocation[];
  locationNote: string | null;
  addressDetail: string | null;
  workingHours: string | null;
  requirements: string[] | null;
  benefits: string[] | null;
  features: string[];
  access: string | null;
  holidays: string | null;
  status: JobStatus;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * 求人一覧クエリのパラメータ型。
 * マスタ共通の MasterListParams に加え、status・companyId で絞り込む。
 */
export interface JobListParams extends MasterListParams {
  status?: JobStatus;
  companyId?: number;
}

/**
 * 求人作成・更新フォームの入力型。
 * id・createdAt・updatedAt はサーバー側で管理するため除外する。
 */
export type JobInput = Partial<Omit<Job, 'id' | 'createdAt' | 'updatedAt'>>;

const BASE = '/jobs';

/**
 * 求人マスタ用 queryOptions factory。
 * `useQuery` / `ensureQueryData` / `prefetchQuery` で使い回す。
 */
export const jobQueries = {
  all: ['jobs'] as const,
  list: (params: JobListParams) =>
    queryOptions({
      queryKey: ['jobs', 'list', params] as const,
      queryFn: async () => {
        const { data } = await apiClient.get<MasterListResponse<Job>>(BASE, {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          query: params.query,
          status: params.status,
          companyId: params.companyId,
        });
        return data;
      },
    }),
  detail: (id: string) =>
    queryOptions({
      queryKey: ['jobs', 'detail', id] as const,
      queryFn: async () => {
        const { data } = await apiClient.get<MasterItemResponse<Job>>(`${BASE}/${id}`);
        return data;
      },
    }),
};

/**
 * 求人作成。
 */
export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: JobInput) => {
      const { data } = await apiClient.post<MasterItemResponse<Job>>(BASE, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: jobQueries.all });
    },
  });
}

/**
 * 求人更新。
 */
export function useUpdateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: { id: string } & JobInput) => {
      const { data } = await apiClient.put<MasterItemResponse<Job>>(`${BASE}/${id}`, input);
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: jobQueries.all });
      qc.invalidateQueries({ queryKey: ['jobs', 'detail', vars.id] });
    },
  });
}

/**
 * 求人ステータス変更（PATCH /jobs/:id/status）。
 * 公開・非公開切り替えなどステータスのみを更新する専用エンドポイント。
 */
export function useUpdateJobStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: JobStatus }) => {
      const { data } = await apiClient.patch<MasterItemResponse<Job>>(`${BASE}/${id}/status`, {
        status,
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: jobQueries.all });
      qc.invalidateQueries({ queryKey: ['jobs', 'detail', vars.id] });
    },
  });
}
